import { Type } from "class-transformer";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObjectNumber } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { EnaioDocumentBaseParams } from "./EnaioDocumentBaseParams";
import { EnaioDocumentFileProperties } from "./EnaioDocumentFileProperties";
import { EnaioDocumentRemark } from "./EnaioDocumentRemark";
import { EnaioDocumentStats } from "./EnaioDocumentStats";
import { EnaioMetaDataField } from "./EnaioMetaDataField";
import { EnaioObjectHistoryEntry } from "./EnaioObjectHistoryEntry";
import { EnaioObjectId } from "./EnaioObjectId";
import { EnaioObjectParentRelation } from "./EnaioObjectParentRelation";
import { EnaioVariantInfo } from "./EnaioVariantInfo";

export class EnaioDocument {
    //netgen start
    id = 0;
    @Type(() => EnaioObjectId)
    archive: EnaioObjectId;
    @Type(() => EnaioObjectId)
    objectType: EnaioObjectId;
    isTypeless = false;
    isInTray = false;
    @Type(() => EnaioMetaDataField)
    metaData: EnaioMetaDataField[];
    @Type(() => EnaioDocumentFileProperties)
    fileProperties: EnaioDocumentFileProperties;
    @Type(() => EnaioDocumentBaseParams)
    baseParams: EnaioDocumentBaseParams;
    @Type(() => EnaioDocumentStats)
    documentStats: EnaioDocumentStats;
    @Type(() => EnaioDocument)
    parent: EnaioDocument;
    @Type(() => EnaioDocument)
    children: EnaioDocument[];
    @Type(() => EnaioVariantInfo)
    variants: EnaioVariantInfo[];
    @Type(() => EnaioDocumentRemark)
    remarks: EnaioDocumentRemark[];
    @Type(() => EnaioObjectHistoryEntry)
    history: EnaioObjectHistoryEntry[];
    documentTempFileName: string;
    documentTempFileNames: string[];
    documentBase64: string;
    documentCouldNotBeConverted = false;
    digest: string;
    @Type(() => EnaioObjectParentRelation)
    parentInfo: EnaioObjectParentRelation[];
    descendantDocumentIds: number[];
    descendantFolderIds: number[];
    childObjectCount: number = null;
    childObjectCountsByType: OrdinaryObjectNumber<number>;
    //netgen end

    getByInternal(internalName: string, throwIfNotFound = true): any {
        let field = this.metaData.find(f => f.field.internalName === internalName);
        if (field == null) {
            if (throwIfNotFound) {
                throw new Error("Das Feld mit dem internen Namen '" + internalName + "' wurde nicht gefunden");
            }
            else {
                return null;
            }
        }
        return field.value;
    }

    getByName(name: string, throwIfNotFound = true): any {
        let field = this.metaData.find(f => f.field.name === name);
        if (field == null) {
            if (throwIfNotFound) {
                throw new Error("Das Feld mit dem Namen '" + name + "' wurde nicht gefunden");
            }
            else {
                return null;
            }
        }
        return field.value;
    }

    getFieldByName(name: string): EnaioMetaDataField {
        return this.metaData.find(f => f.field.name === name);
    }

    getFieldByInternalName(internalName: string): EnaioMetaDataField {
        return this.metaData.find(f => f.field.internalName === internalName);
    }

    getStringByInternal(internalName: string): string {
        return Objects.toString(this.getByInternal(internalName));
    }

    getStringByName(name: string): string {
        return Objects.toString(this.getByName(name));
    }

    getNumberByInternal(internalName: string): number {
        return Utils.toNumber(this.getByInternal(internalName), false, true);
    }

    getNumberByName(name: string): number {
        return Utils.toNumber(this.getByName(name), false, true);
    }

    getBoolByInternal(internalName: string): boolean {
        return Utils.toBool(this.getByInternal(internalName));
    }

    getBoolByName(name: string): boolean {
        return Utils.toBool(this.getByName(name));
    }

    getDateByInternal(internalName: string): Date {
        return this.toDate(this.getStringByInternal(internalName));
    }

    getDateByName(name: string): Date {
        return this.toDate(this.getStringByName(name));
    }

    getMetaDataMap(): any {
        return Arrays.toPlainMap(this.metaData, f => f.field.internalName, f => f.value);
    }

    getBestGuessFileName(): string {
        let plain = this.documentStats?.name.toUpperCase().endsWith(".CMEF") && !Utils.isNoe(this.documentTempFileName) ? this.documentTempFileName :
            Utils.isNoe(this.documentStats?.name) ? this.documentTempFileName : this.documentStats.name;
        return Utils.getFileNameWithoutPath(plain);
    }

    private toDate(s: string): Date {
        if (s == "") {
            return null;
        }
        else {
            try {
                return Dates.fromString(s, "DD.MM.YYYY");
            }
            catch {
                return new Date(Date.parse(s));
            }
        }
    }


}